import React, { useEffect, useState } from "react";
import "./Review.css";
import { getAllFriendsWithSharedReviews } from "../../HTTP/user";
import ArticleBlocks from "../Review/ArticleBlocks";
import { addArticlesToReview } from "../../HTTP/articles";
import { goToStaticPage } from "../../Helper/utils";

const getProfilePictureUrl = (profilePicture) => {
  if (!profilePicture) {
    return process.env.PUBLIC_URL + "/profile_default.png";
  }

  if (profilePicture.startsWith("//")) {
    return `${window.location.protocol}${profilePicture}`;
  }

  return profilePicture;
};

const formatCreationDate = (dateValue) => {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const AddToFriendReview = () => {
  const params = new URLSearchParams(window.location.search);
  const articles = JSON.parse(decodeURIComponent(params.get("data")));

  articles.forEach((item) => {
    delete item.publication_date;
    delete item.id;
  });

  const [friends, setFriends] = useState([]);
  const [selectedFriendId, setSelectedFriendId] = useState("");
  const [selectedReviewId, setSelectedReviewId] = useState("");
  const [step, setStep] = useState(1);

  useEffect(() => {
    const fetchFriends = async () => {
      const friendsData = await getAllFriendsWithSharedReviews();
      setFriends(friendsData || []);
    };

    fetchFriends();
  }, []);

  const selectedFriend = friends.find(
    (friend) => String(friend.id) === String(selectedFriendId)
  );

  const selectedReview = selectedFriend?.reviews?.find(
    (review) => String(review.id) === String(selectedReviewId)
  );

  const handleSelectFriend = (friendId) => {
    setSelectedFriendId(String(friendId));
    setSelectedReviewId("");
    setStep(2);
  };

  const handleBackToUsers = () => {
    setStep(1);
    setSelectedFriendId("");
    setSelectedReviewId("");
  };

  const handleAddArticles = () => {
    if (!selectedFriend || !selectedReviewId) {
      console.log("Please select a friend and a review");
      return;
    }

    addArticlesToReview(articles, selectedReviewId).then((res) => {
      if (res === true) {
        alert("Articles added !");
        goToStaticPage(false, selectedReviewId);
      } else {
        alert("Error");
      }
    });
  };

  return (
    <div className="m-4">
      <h1>Ajouter à une revue de presse d'un ami</h1>
      <ArticleBlocks articles={articles} />

      {step === 1 && (
        <>
          <h2 className="text-lg font-bold mt-6 mb-3">1. Choix de l'utilisateur</h2>
          {friends.length === 0 && (
            <p className="text-gray-600 mb-4">Aucun utilisateur disponible.</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {friends.map((friend) => (
              <button
                key={friend.id}
                type="button"
                onClick={() => handleSelectFriend(friend.id)}
                className="text-left rounded-lg border p-3 transition border-gray-200 bg-white hover:border-cyan-300"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={getProfilePictureUrl(friend.profile_picture)}
                    alt={`${friend.firstname} ${friend.lastname}`}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold notranslate">
                      {friend.lastname} {friend.firstname}
                    </p>
                    <p className="text-sm text-gray-600">{friend.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {friend.reviews?.length || 0} revue(s)
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {step === 2 && selectedFriend && (
        <>
          <div className="flex items-center justify-between mt-6 mb-3">
            <h2 className="text-lg font-bold">2. Choix de la revue de presse</h2>
            <button
              type="button"
              onClick={handleBackToUsers}
              className="bg-gray-200 hover:bg-gray-300 text-black font-semibold py-2 px-3 rounded"
            >
              Retour au choix utilisateur
            </button>
          </div>

          <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3 mb-4">
            <p className="text-sm text-gray-700">
              Utilisateur choisi: <strong>{selectedFriend.lastname} {selectedFriend.firstname}</strong>
            </p>
          </div>

          {(!selectedFriend.reviews || selectedFriend.reviews.length === 0) && (
            <p className="text-gray-600 mb-4">
              Cet utilisateur n'a pas de revue de presse partageable.
            </p>
          )}

          {selectedFriend.reviews && selectedFriend.reviews.length > 0 && (
            <div className="space-y-3 max-h-[40vh] overflow-y-auto mb-6">
              {selectedFriend.reviews.map((review) => {
                const isSelected = String(review.id) === String(selectedReviewId);
                return (
                  <button
                    key={review.id}
                    type="button"
                    onClick={() => setSelectedReviewId(String(review.id))}
                    className={`w-full text-left rounded-lg border p-4 transition ${
                      isSelected
                        ? "border-yellow-500 bg-yellow-50"
                        : "border-gray-200 bg-white hover:border-yellow-300"
                    }`}
                  >
                    <p className="font-semibold">{review.theme}</p>
                    <p className="text-sm text-gray-700 whitespace-pre-line mt-1">
                      {review.presentation || "-"}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Créée le {formatCreationDate(review.creation_date)}
                    </p>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-6">
            {selectedReview && (
              <p className="text-sm text-gray-700 mb-3">
                Revue sélectionnée: <strong>{selectedReview.theme}</strong>
              </p>
            )}
            <button
              type="button"
              className={`font-bold py-2 px-4 rounded ${
                selectedReviewId
                  ? "bg-yellow-400 text-black"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
              onClick={handleAddArticles}
              disabled={!selectedReviewId}
            >
              Partager dans cette revue
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AddToFriendReview;
